import { FC, memo } from 'react'

import { Button, Popover, Stack, useTheme } from '@mui/material'

import { useTranslation } from 'react-i18next'

import useModal from 'src/hooks/useModal'
import { useMentorCloudDeleteFolder } from 'src/hooks/files/useMentorCloudDeleteFolder'

import DeleteConfirmationModal from 'src/components/common/modals/DeleteConfirmationModal'

import { MentorCloudRenameModal } from '../MentorCloudRenameModal/MentorCloudRenameModal'

import { Folder, MentorCloudFileFilters } from 'src/types/files/file'

interface MentorCloudFolderPopoverProps {
  parentFolderId?: number
  filters: MentorCloudFileFilters
  id?: string
  isOpen: boolean
  anchor: Element | null
  folder: Folder | null
  close: () => void
  onChangePopoverFolder: (file: null) => void
}

export const MentorCloudFolderPopover: FC<MentorCloudFolderPopoverProps> = memo(props => {
  const { parentFolderId, id, filters, anchor, isOpen, folder, close, onChangePopoverFolder } = props

  const { t } = useTranslation()

  const { isOpen: isDeleteModalOpen, close: closeDeleteModal, open: openDeleteModal } = useModal()
  const { isOpen: isRenameModalOpen, close: closeRenameModal, open: openRenameModal } = useModal()

  const theme = useTheme()
  const { deleteFolder } = useMentorCloudDeleteFolder(filters, parentFolderId || null)

  const onRenameFile = () => {
    openRenameModal()
    close()
  }

  const onDeleteFile = async () => {
    close()
    if (!folder) return

    await deleteFolder({
      filter_by: [
        {
          attribute: 'id',
          operator: 'in',
          value: [folder.id]
        }
      ]
    })
    onChangePopoverFolder(null)
  }

  return (
    <>
      <MentorCloudRenameModal
        filters={filters}
        isOpen={isRenameModalOpen}
        onClose={closeRenameModal}
        fileId={folder?.id}
        fileName={folder?.name}
        parentFolderId={parentFolderId}
        type='folder'
      />
      <Popover
        id={id}
        open={isOpen}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack gap={theme.spacing(1)} padding={theme.spacing(1)}>
          <Button sx={{ minWidth: theme.spacing(32) }} fullWidth onClick={onRenameFile} variant='text'>
            {t('ACTION.rename')}
          </Button>
          <Button sx={{ minWidth: theme.spacing(32) }} onClick={openDeleteModal} variant='tonal' color='error'>
            {t('ACTION.delete')}
          </Button>
        </Stack>
      </Popover>
      <DeleteConfirmationModal
        entityType='file'
        confirmWord='DELETE'
        preventContextMenu
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onCancel={closeDeleteModal}
        onDelete={onDeleteFile}
      />
    </>
  )
})
